import { memo } from 'react';

/**
 * The small markdown subset the OS actually emits: headings, bullets, numbered
 * lists, blockquotes, bold, and links.
 *
 * Deliberately not a markdown library. This started life inside the James chat
 * bubble and was promoted here when the sales research brief needed the same
 * rendering — one parser, rather than a second one drifting alongside it. It
 * renders React nodes throughout, so there is no `dangerouslySetInnerHTML` and
 * nothing in a model's output can inject markup.
 */

const LINK_OR_BOLD = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

export function renderInlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  LINK_OR_BOLD.lastIndex = 0;
  while ((match = LINK_OR_BOLD.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));

    if (match[1] !== undefined) {
      parts.push(
        <strong key={match.index} className="font-semibold">
          {match[1]}
        </strong>,
      );
    } else {
      parts.push(
        <a
          key={match.index}
          href={match[3]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 hover:no-underline"
        >
          {match[2]}
        </a>,
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : text;
}

/** A GFM table row: `| a | b |`, with `| --- | ---: |` underneath the header. */
const TABLE_ROW = /^\s*\|(.+)\|\s*$/;
const TABLE_DIVIDER = /^\s*\|[\s:|-]+\|\s*$/;

function splitRow(row: string): string[] {
  return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

/**
 * The contact tables and the bid-team country counts are the parts of a brief
 * a rep acts on directly, so they have to render as tables rather than as rows
 * of pipe characters.
 */
function MarkdownTable({ rows }: { rows: string[] }) {
  const header = splitRow(rows[0] ?? '');
  const alignments = splitRow(rows[1] ?? '').map((c) =>
    c.endsWith(':') ? (c.startsWith(':') ? 'center' : 'right') : 'left',
  );
  const body = rows.slice(2).map(splitRow);

  return (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-body-xs">
        <thead>
          <tr>
            {header.map((cell, i) => (
              <th
                key={i}
                className="border border-border bg-surface-secondary px-2 py-1 font-semibold"
                style={{ textAlign: alignments[i] ?? 'left' }}
              >
                {renderInlineMarkdown(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  className="border border-border px-2 py-1 align-top break-words"
                  style={{ textAlign: alignments[c] ?? 'left' }}
                >
                  {renderInlineMarkdown(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Groups consecutive table rows into one block so they render as one table. */
function toBlocks(lines: string[]): ({ table: string[] } | { line: string })[] {
  const blocks: ({ table: string[] } | { line: string })[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (TABLE_ROW.test(line) && TABLE_DIVIDER.test(lines[i + 1] ?? '')) {
      const table: string[] = [];
      while (i < lines.length && TABLE_ROW.test(lines[i] ?? '')) table.push(lines[i++] as string);
      i--;
      blocks.push({ table });
    } else {
      blocks.push({ line });
    }
  }
  return blocks;
}

export const SimpleMarkdown = memo(function SimpleMarkdown({ content }: { content: string }) {
  const blocks = toBlocks(content.split('\n'));

  return (
    <>
      {blocks.map((block, i) => {
        if ('table' in block) return <MarkdownTable key={i} rows={block.table} />;
        const line = block.line;
        if (line.startsWith('### ')) {
          return (
            <h4 key={i} className="mb-1 mt-4 text-body-sm font-semibold first:mt-0">
              {renderInlineMarkdown(line.slice(4))}
            </h4>
          );
        }
        if (line.startsWith('## ')) {
          return (
            <h3 key={i} className="mb-1 mt-4 text-body-md font-semibold first:mt-0">
              {renderInlineMarkdown(line.slice(3))}
            </h3>
          );
        }
        if (line.startsWith('# ')) {
          return (
            <h2 key={i} className="mb-2 mt-5 text-heading-sm font-semibold first:mt-0">
              {renderInlineMarkdown(line.slice(2))}
            </h2>
          );
        }

        if (line.startsWith('> ')) {
          return (
            <blockquote
              key={i}
              className="my-2 border-l-2 border-border pl-3 text-text-secondary italic"
            >
              {renderInlineMarkdown(line.slice(2))}
            </blockquote>
          );
        }

        // Nested bullets are indented rather than given a second list level —
        // the reports only ever go one deep.
        const nested = line.match(/^ {2,}[-*]\s/);
        if (nested) {
          return (
            <div key={i} className="ml-6 flex gap-2 text-text-secondary">
              <span className="text-text-tertiary">◦</span>
              <span>{renderInlineMarkdown(line.slice(nested[0].length))}</span>
            </div>
          );
        }

        if (line.startsWith('- ') || line.startsWith('* ')) {
          return (
            <div key={i} className="ml-1 flex gap-2">
              <span className="text-text-tertiary">•</span>
              <span>{renderInlineMarkdown(line.slice(2))}</span>
            </div>
          );
        }

        const numbered = line.match(/^(\d+)\.\s/);
        if (numbered) {
          return (
            <div key={i} className="ml-1 flex gap-2">
              <span className="min-w-[1.25rem] text-text-tertiary">{numbered[1]}.</span>
              <span>{renderInlineMarkdown(line.slice(numbered[0].length))}</span>
            </div>
          );
        }

        if (!line.trim()) return <div key={i} className="h-2" />;

        return <p key={i}>{renderInlineMarkdown(line)}</p>;
      })}
    </>
  );
});
